import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import { Model } from "survey-core";

import useCreateSurveyResponse from "./useCreateSurveyResponse";
import useFetchSurveyResponse from "./useFetchSurveyResponse";

import { surveyContentFormatter } from "../utils/surveys";
import { applyDefaultValuesToSurvey } from "../utils/applicationPageLogic";

function isSurveyContentEmpty(surveyContent) {
  if (!surveyContent) return true;
  return !(Array.isArray(surveyContent.pages) && surveyContent.pages.length > 0);
}

function normalizeAnswerForChoices(question, answer) {
  const type = question?.getType?.();
  const isChoiceQ = ["dropdown", "radiogroup", "checkbox"].includes(type);

  if (!isChoiceQ || !Array.isArray(question?.choices)) return answer;

  const choices = question.choices.map((choice) =>
    typeof choice === "object" ? { value: choice.value, text: choice.text ?? choice.value } : { value: choice, text: choice },
  );

  const normalizeOne = (value) => {
    if (choices.some((choice) => choice.value === value)) return value;
    const byText = choices.find((choice) => choice.text === value);
    return byText ? byText.value : value;
  };

  return Array.isArray(answer) ? answer.map(normalizeOne) : normalizeOne(answer);
}

function isSurveyComplete(model) {
  if (!model) return false;
  const requiredVisibleQuestions = model.getAllQuestions().filter((q) => q.isVisible && q.isRequired);
  return requiredVisibleQuestions.every((q) => q.isAnswered);
}

function buildSurveyModel(surveyContent) {
  const model = new Model(surveyContent);
  model.validateOnValueChange = false;
  model.showNavigationButtons = false;
  model.showErrorOnCompleted = true;
  model.checkErrorsMode = "onNextPage";
  model.storeOthersAsComment = false;
  model.showQuestionNumbers = "off";
  return model;
}

export default function usePhaseQuestionnaireNavigation({ questionnaires, assignappId, applicationId, advancePhase, navigate, routes }) {
  const { createSurveyResponse } = useCreateSurveyResponse();
  const { fetchSurveyResponse, data: surveyResponseData, loading: responseLoading, error: fetchSurveyError } = useFetchSurveyResponse();

  const [currentQuestionnaireIndex, setCurrentQuestionnaireIndex] = useState(0);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [isQuestionnaireCompleteState, setIsQuestionnaireCompleteState] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasFetchedQuestionnaireResponse, setHasFetchedQuestionnaireResponse] = useState(false);
  const [canRenderSurvey, setCanRenderSurvey] = useState(false);

  const hasCreatedInitialResponse = useRef(false);

  const hasData = questionnaires.length > 0;

  const currentQuestionnaire = useMemo(() => {
    if (!hasData) return null;
    return questionnaires[currentQuestionnaireIndex] ?? null;
  }, [hasData, questionnaires, currentQuestionnaireIndex]);

  const currentSurveyId = currentQuestionnaire?._id?.$oid;
  const currentAssignappQuestionnaireId = currentQuestionnaire?.assignapp_questionnaire_id;
  const currentSurveyContent = currentQuestionnaire?.survey_content;

  const surveyModel = useMemo(() => {
    if (!currentQuestionnaire || !canRenderSurvey) return null;
    if (isSurveyContentEmpty(currentSurveyContent)) return null;
    return buildSurveyModel(currentSurveyContent);
  }, [currentQuestionnaire, currentSurveyContent, canRenderSurvey]);

  const isLastQuestionnaire = useMemo(
    () => currentQuestionnaireIndex === questionnaires.length - 1,
    [currentQuestionnaireIndex, questionnaires.length],
  );

  const progressValue = useMemo(() => {
    if (!questionnaires.length) return 0;
    return ((currentQuestionnaireIndex + 1) / questionnaires.length) * 100;
  }, [currentQuestionnaireIndex, questionnaires.length]);

  const openError = useCallback((message) => {
    setErrorMessage(message);
    setShowError(true);
  }, []);

  useEffect(() => {
    if (!currentQuestionnaire) return;

    if (isSurveyContentEmpty(currentSurveyContent)) {
      openError("El cuestionario actual no existe o no contiene preguntas válidas.");
    }
  }, [currentQuestionnaire, currentSurveyContent, openError]);

  useEffect(() => {
    if (!hasData || !assignappId || !currentSurveyId || !currentAssignappQuestionnaireId) return;

    setCanRenderSurvey(false);
    fetchSurveyResponse(currentSurveyId, assignappId, currentAssignappQuestionnaireId);
  }, [hasData, assignappId, currentSurveyId, currentAssignappQuestionnaireId, fetchSurveyResponse]);

  useEffect(() => {
    if (!responseLoading) {
      setHasFetchedQuestionnaireResponse(true);
      setCanRenderSurvey(!fetchSurveyError);
    }
  }, [responseLoading, fetchSurveyError]);

  useEffect(() => {
    hasCreatedInitialResponse.current = false;
    setHasFetchedQuestionnaireResponse(false);
    setCanRenderSurvey(false);
    setUnsavedChanges(false);
    setIsQuestionnaireCompleteState(false);
  }, [currentQuestionnaireIndex]);

  useEffect(() => {
    if (!surveyModel) return;
    setIsQuestionnaireCompleteState(isSurveyComplete(surveyModel));
  }, [surveyModel]);

  useEffect(() => {
    if (!surveyModel || !currentSurveyId || responseLoading) return;

    const content = surveyResponseData?.content;
    if (!content) return;
    if (surveyResponseData?.survey_id?.$oid !== currentSurveyId) return;

    const updatedSurveyJson = applyDefaultValuesToSurvey(currentSurveyContent, content);
    surveyModel.fromJSON(updatedSurveyJson);

    const restoredEntries = content
      .filter((item) => item && Object.prototype.hasOwnProperty.call(item, "questionAnswer"))
      .map((item) => {
        const question = surveyModel.getQuestionByName(item.questionId);
        if (!question) return null;
        return [item.questionId, normalizeAnswerForChoices(question, item.questionAnswer)];
      })
      .filter(Boolean);

    if (restoredEntries.length) {
      const restoredData = Object.fromEntries(restoredEntries);

      if (typeof surveyModel.setValue === "function") {
        Object.entries(restoredData).forEach(([key, value]) => surveyModel.setValue(key, value));
      } else if (typeof surveyModel.mergeData === "function") {
        surveyModel.mergeData(restoredData);
      } else {
        surveyModel.data = restoredData;
      }
    }

    setIsQuestionnaireCompleteState(isSurveyComplete(surveyModel));

    // Restaura la página donde quedó el usuario
    const savedPageName = surveyResponseData?.last_page_name;
    if (savedPageName) {
      const targetPage = surveyModel.getPageByName(savedPageName);
      if (targetPage?.isVisible) {
        surveyModel.currentPage = targetPage;
      }
    }
  }, [surveyModel, surveyResponseData, responseLoading, currentSurveyId, currentSurveyContent]);

  useEffect(() => {
    if (!surveyModel) return;

    const handleChange = () => {
      setUnsavedChanges(true);
      setIsQuestionnaireCompleteState(isSurveyComplete(surveyModel));
    };

    surveyModel.onValueChanged.add(handleChange);
    surveyModel.onCurrentPageChanged.add(handleChange);
    surveyModel.onQuestionVisibleChanged.add(handleChange);

    return () => {
      surveyModel.onValueChanged.remove(handleChange);
      surveyModel.onCurrentPageChanged.remove(handleChange);
      surveyModel.onQuestionVisibleChanged.remove(handleChange);
    };
  }, [surveyModel]);

  useEffect(() => {
    if (!unsavedChanges || !surveyModel || !hasFetchedQuestionnaireResponse || !currentSurveyId || !currentAssignappQuestionnaireId) {
      return;
    }

    const currentStatus = surveyResponseData?.status ?? currentQuestionnaire?.status;
    const statusToSend = currentStatus === "started" || currentStatus === "finished" ? currentStatus : "started";

    const contentToSend = surveyContentFormatter(surveyModel.data, surveyModel);

    if (!Array.isArray(contentToSend) || contentToSend.length === 0) {
      setUnsavedChanges(false);
      return;
    }

    createSurveyResponse({
      id: currentSurveyId,
      assignapp_id: assignappId,
      assignapp_questionnaire_id: currentAssignappQuestionnaireId,
      content: contentToSend,
      status: statusToSend,
      last_page_name: surveyModel.currentPage?.name ?? null,
    });

    setUnsavedChanges(false);
  }, [
    unsavedChanges,
    surveyModel,
    hasFetchedQuestionnaireResponse,
    currentSurveyId,
    currentAssignappQuestionnaireId,
    assignappId,
    surveyResponseData?.status,
    currentQuestionnaire?.status,
    createSurveyResponse,
  ]);

  useEffect(() => {
    const ready =
      surveyModel &&
      assignappId &&
      hasData &&
      hasFetchedQuestionnaireResponse &&
      currentSurveyId &&
      currentAssignappQuestionnaireId &&
      !hasCreatedInitialResponse.current &&
      !isSurveyContentEmpty(currentSurveyContent);

    if (!ready) return;

    const alreadyFinished = surveyResponseData?.status === "finished" || currentQuestionnaire?.status === "finished";
    const alreadyStarted = surveyResponseData?.status === "started" || currentQuestionnaire?.status === "started";

    if (!alreadyFinished && !alreadyStarted) {
      createSurveyResponse({
        id: currentSurveyId,
        assignapp_id: assignappId,
        assignapp_questionnaire_id: currentAssignappQuestionnaireId,
        content: [],
        status: "started",
      });

      hasCreatedInitialResponse.current = true;
    }
  }, [
    surveyModel,
    assignappId,
    hasData,
    hasFetchedQuestionnaireResponse,
    surveyResponseData?.status,
    currentSurveyId,
    currentAssignappQuestionnaireId,
    currentSurveyContent,
    currentQuestionnaire?.status,
    createSurveyResponse,
  ]);

  const handleFinishQuestionnaire = useCallback(async () => {
    if (!surveyModel || !hasData) return;

    const isValid = surveyModel.validate(true);
    if (!isValid) {
      setIsQuestionnaireCompleteState(false);
      return;
    }

    const contentToSend = surveyContentFormatter(surveyModel.data, surveyModel);

    if (currentSurveyId && currentAssignappQuestionnaireId) {
      await createSurveyResponse({
        id: currentSurveyId,
        assignapp_id: assignappId,
        assignapp_questionnaire_id: currentAssignappQuestionnaireId,
        content: contentToSend,
        status: "finished",
      });
    }

    if (!isLastQuestionnaire) {
      setCurrentQuestionnaireIndex((prev) => prev + 1);
      setIsQuestionnaireCompleteState(false);
      return;
    }

    const response = await advancePhase(assignappId);
    const result = response?.data;

    if (!result) {
      throw new Error(response?.error?.message || "No fue posible avanzar la fase");
    }

    if (result.status === "complete") {
      navigate(routes.root, { replace: true });
      return;
    }

    if (result.current_phase === "in_app") {
      navigate(`${routes.aplicacion}?id=${applicationId}`, { replace: true });
      return;
    }

    if (["pre", "post"].includes(result.current_phase)) {
      navigate(`${routes.cuestionariosPorFase}?id=${applicationId}&phase=${result.current_phase}`, { replace: true });
      return;
    }

    navigate(routes.root, { replace: true });
  }, [
    surveyModel,
    hasData,
    currentSurveyId,
    currentAssignappQuestionnaireId,
    assignappId,
    isLastQuestionnaire,
    createSurveyResponse,
    advancePhase,
    navigate,
    applicationId,
    routes,
  ]);

  useEffect(() => {
    if (fetchSurveyError) {
      openError(fetchSurveyError.message || "Error al cargar la respuesta del cuestionario.");
    }
  }, [fetchSurveyError, openError]);

  return {
    currentQuestionnaire,
    currentQuestionnaireIndex,
    totalQuestionnaires: questionnaires.length,
    surveyModel,
    canRenderSurvey,
    responseLoading,
    showError,
    errorMessage,
    setShowError,
    isQuestionnaireComplete: isQuestionnaireCompleteState,
    progressValue,
    isLastQuestionnaire,
    handleFinishQuestionnaire,
  };
}
